/**
 * Open a task OVER the page you are on (Corbin, 10/16: "when there is a task
 * on a job page and you click do it now it takes you to the task page instead
 * of keeping you in the job and just bringing the flyout over the job page").
 *
 * Task links come from the server as `/inbox?task=<id>` or `/tasks?task=<id>`.
 * Following one used to leave the job (or the Grid, or wherever you were) for
 * the Tasks page. Now any "open this task" button calls `openTaskHere`: it
 * slides the task drawer in over the current screen, and closing it leaves you
 * exactly where you were. The drawer itself lives once, in the app shell
 * (components/task-flyout-host.tsx).
 *
 * On the Tasks page itself the page already owns its drawer through `?task=`,
 * so there the link is followed as before.
 */

export const TASK_FLYOUT_EVENT = "gwc:open-task-flyout";

/** The task id in a task link, or null when the link is not a task link. */
export function taskIdFromHref(href: string | null | undefined): string | null {
    if (!href) return null;
    try {
        const u = new URL(href, "http://local.invalid");
        if (u.pathname !== "/inbox" && u.pathname !== "/tasks") return null;
        const id = u.searchParams.get("task");
        return id && id.trim() ? id : null;
    } catch {
        return null;
    }
}

function onTasksPage(): boolean {
    if (typeof window === "undefined") return false;
    const p = window.location.pathname;
    return p === "/inbox" || p === "/tasks";
}

/** Slide the task drawer in over the current page. */
export function openTaskFlyout(taskId: string): void {
    window.dispatchEvent(new CustomEvent<{ taskId: string }>(TASK_FLYOUT_EVENT, { detail: { taskId } }));
}

/**
 * Open a task link where you are. A task link opens the drawer over this page;
 * anything else (or any link while on the Tasks page) is followed as before.
 */
export function openTaskHere(href: string, navigate: (to: string) => void): void {
    const id = taskIdFromHref(href);
    if (id && !onTasksPage()) openTaskFlyout(id);
    else navigate(href);
}
